/**
 * Unified (cross-provider) models for the app. The grouping lives in
 * `@bedrock-profiles/shared` so the build scripts produce the same slugs,
 * names and descriptions as the browser.
 */
import {
  findCatalogModel,
  generateModelSlug as generateSharedModelSlug,
  groupIntoCatalogModels,
  type CatalogModel,
  type CatalogModelDeployment,
  type ModelOffering,
} from '@bedrock-profiles/shared';

export type {
  ModelBenchmark,
  ProviderNotes,
} from '@bedrock-profiles/shared';
export {
  buildCatalogModelIdentityIndex,
  fallbackModelDescription,
  getOfferingCapabilities,
  getOfferingFeatureFlags,
  getOfferingTokenPricing,
} from '@bedrock-profiles/shared';

export type UnifiedModel = CatalogModel;
export type ModelDeployment = CatalogModelDeployment;

/**
 * URL slug for a single provider model ID (no `/`, `@` or encoded characters).
 * Prefer `UnifiedModel.slug`, which is the canonical slug of the merged model.
 */
export function generateModelSlug(key: string): string {
  return generateSharedModelSlug(key);
}

export function groupIntoUnifiedModels(offerings: ModelOffering[]): UnifiedModel[] {
  return groupIntoCatalogModels(offerings);
}

/** Find a model by canonical slug, alias, legacy slug or raw provider model ID. */
export function findUnifiedModel(models: UnifiedModel[], key: string): UnifiedModel | undefined {
  return findCatalogModel(models, key);
}

/** The model page path for a provider model ID, using the merged model's slug when known. */
export function modelPathForModelId(models: UnifiedModel[], modelId: string): string {
  const model = models.find((m) => m.deployments.some((d) => d.modelId === modelId));
  return `/models/${model?.slug ?? generateModelSlug(modelId)}`;
}

/**
 * Get unique cloud providers from deployments
 */
export function getCloudProviders(model: UnifiedModel): string[] {
  return Array.from(new Set(model.deployments.map((d) => d.cloudProvider)));
}

/**
 * Get minimum pricing across all deployments
 */
export function getMinPricing(model: UnifiedModel): { input: number; output: number } | null {
  let minInput = Infinity;
  let minOutput = Infinity;
  let hasPricing = false;

  for (const deployment of model.deployments) {
    if (deployment.pricing) {
      hasPricing = true;
      if (deployment.pricing.input < minInput) minInput = deployment.pricing.input;
      if (deployment.pricing.output < minOutput) minOutput = deployment.pricing.output;
    }
  }

  return hasPricing ? { input: minInput, output: minOutput } : null;
}

/**
 * Get total unique regions across all deployments
 */
export function getTotalRegionCount(model: UnifiedModel): number {
  const regions = new Set<string>();
  for (const deployment of model.deployments) {
    deployment.regions.forEach((r) => regions.add(r));
  }
  return regions.size;
}
